import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Modal, Form, message, Checkbox, Row, Col, Input } from "antd";
import { useContext, useEffect, useState } from "react";
import { handleCompleteAutoRequest } from "../../../api/autorequests";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { AuthContext } from "../../../contexts/AuthContext";

export default function CompleteRequest({
  isVisible,
  setVisible,
  selected,
}: any) {
  const { user } = useContext(AuthContext);
  const { _id } = selected;
  const queryClient = useQueryClient();
  const [hasCreds, setHasCreds] = useState(false);
  const [form] = Form.useForm();

  const { mutate, isPending } = useMutation({
    mutationFn: handleCompleteAutoRequest,
    onSuccess: () => {
      message.success("Completed Successfully");
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      queryClient.invalidateQueries({ queryKey: ["autorequests"] });
      queryClient.invalidateQueries({ queryKey: ["unitRequests"] });
    },
    onError: () => {
      message.error("Something went wrong");
    },
  });

  const onSubmit = async () => {
    const values = form.getFieldsValue();
    const data = { userId: user?._id, ...values };
    await mutate({ _id, data });
    setVisible(false);
  };

  const clearForm = async () => {
    form.resetFields();
  };

  useEffect(() => {
    form.resetFields();
  }, [selected, form]);

  return (
    <Modal
      open={isVisible}
      title="Complete Request"
      onCancel={() => setVisible(false)}
      footer={[
        <Button key="back" onClick={clearForm}>
          Clear
        </Button>,
        <Button
          key="submit"
          form="completeAutorequest"
          htmlType="submit"
          type="primary"
          onSubmit={onSubmit}
          loading={isPending}
        >
          Complete
        </Button>,
      ]}
    >
      <Form
        id="completeAutorequest"
        form={form}
        name="normal_login"
        className="login-form"
        onFinish={onSubmit}
        layout="vertical"
      >
        <div className="flex flex-col items-center justify-center my-8">
          <div className="font-bold text-black text-xl">
            ✅ Complete Request
          </div>
          <div className="font-bold text-lg capitalize">
            Role: {selected?.designation} for {selected?.name}
          </div>
          <span className="text-gray-700">
            {selected?.companies?.map((company: any) => company).join(", ") ||
              "-"}
          </span>
          {selected?.isNewUser && (
            <div className="">
              <div className="">
                <div className="my-2">
                  <Checkbox
                    checked={hasCreds}
                    onChange={() => setHasCreds(!hasCreds)}
                  >
                    <div className="flex items-center">
                      Share Credentials
                      <div className="text-red-600 mx-1">
                        *This is a new User
                      </div>
                    </div>
                  </Checkbox>
                </div>

                {hasCreds && (
                  <div className="my-2">
                    <Form
                      id="sendCreds"
                      form={form}
                      name="normal_login"
                      className="login-form"
                      onFinish={onSubmit}
                      layout="vertical"
                    >
                      <Row gutter={[16, 0]}>
                        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                          <Form.Item
                            name="username"
                            label="Username"
                            rules={[
                              {
                                required: true,
                                message: "Please enter username",
                              },
                            ]}
                          >
                            <Input
                              aria-label="Username"
                              name="name"
                              type="text"
                              size="large"
                              required
                              className="flex flex-row justify-center "
                              placeholder="Name"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                          <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                              {
                                required: false,
                                message: "Please enter password",
                              },
                            ]}
                          >
                            <Input.Password
                              aria-label="Password"
                              name="password"
                              type="password"
                              size="large"
                              required
                              className="flex flex-row justify-center"
                              placeholder="Password"
                              iconRender={(visible) =>
                                visible ? (
                                  <EyeTwoTone />
                                ) : (
                                  <EyeInvisibleOutlined />
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Form>
    </Modal>
  );
}
